import React from 'react';
import { XMarkIcon, ClockIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Button } from '@/ui/components/atoms/Button';
import { useResponsableHistory } from '../hooks/useAffaireSettings';

interface ResponsableHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  affaireId: number;
}

export const ResponsableHistory: React.FC<ResponsableHistoryProps> = ({
  isOpen,
  onClose,
  affaireId,
}) => {
  const { history, isLoading, error } = useResponsableHistory(affaireId);

  if (!isOpen) return null;

  const formatUserName = (user: any) => {
    if (!user || !user.id) return 'Aucun';
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Utilisateur inconnu';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-BE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getReasonLabel = (reason: string) => {
    const labels: Record<string, string> = {
      manual: 'Changement manuel',
      auto_sync_rv: 'Synchronisation automatique (RV)',
      auto_init: 'Initialisation automatique',
      auto_sync_enc: 'Synchronisation automatique (Encodeur)',
    };
    return labels[reason] || reason;
  };

  const getReasonColor = (reason: string) => {
    const colors: Record<string, string> = {
      manual: 'bg-blue-100 text-blue-800',
      auto_sync_rv: 'bg-green-100 text-green-800',
      auto_init: 'bg-gray-100 text-gray-800',
      auto_sync_enc: 'bg-purple-100 text-purple-800',
    };
    return colors[reason] || 'bg-gray-100 text-gray-800';
  };

  const getLineLabel = (line: string) => {
    const labels: Record<string, string> = {
      primary: '1ère ligne (Principal)',
      second: '2ème ligne (RV)',
      third: '3ème ligne',
    };
    return labels[line] || line;
  };

  const getLineBadgeColor = (line: string) => {
    const colors: Record<string, string> = {
      primary: 'bg-indigo-100 text-indigo-800',
      second: 'bg-teal-100 text-teal-800',
      third: 'bg-amber-100 text-amber-800',
    };
    return colors[line] || 'bg-gray-100 text-gray-800';
  };

  return (
    <>
      {/* Backdrop */}
      <div className="tracker-modal-backdrop" onClick={onClose} />

      {/* Modal */}
      <div className="tracker-modal tracker-modal-large">
        <div className="tracker-modal-header">
          <h3 className="tracker-modal-title">
            <ClockIcon className="w-5 h-5" />
            <span>Historique des responsables</span>
          </h3>
          <button
            className="tracker-modal-close"
            onClick={onClose}
            aria-label="Fermer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="tracker-modal-content">
          {isLoading && (
            <div className="text-center py-8 text-gray-500">
              Chargement de l'historique...
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                Erreur lors du chargement de l'historique: {error}
              </p>
            </div>
          )}

          {!isLoading && !error && history.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Aucun historique disponible pour ce dossier.
            </div>
          )}

          {!isLoading && !error && history.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Historique complet de tous les changements de responsables pour garantir la transparence du dossier.
              </p>

              <div className="tracker-history-timeline">
                {history.map((entry, index) => (
                  <div key={entry.id} className="tracker-history-item">
                    {/* Timeline connector */}
                    {index < history.length - 1 && (
                      <div className="tracker-history-connector" />
                    )}

                    {/* Timeline dot */}
                    <div className="tracker-history-dot" />

                    {/* Content */}
                    <div className="tracker-history-content">
                      {/* Header */}
                      <div className="tracker-history-header">
                        <span className={`tracker-history-line-badge ${getLineBadgeColor(entry.responsable_line)}`}>
                          {getLineLabel(entry.responsable_line)}
                        </span>
                        <span className={`tracker-history-reason-badge ${getReasonColor(entry.change_reason)}`}>
                          {getReasonLabel(entry.change_reason)}
                        </span>
                        <span className="tracker-history-date">
                          {formatDate(entry.changed_at)}
                        </span>
                      </div>

                      {/* Change details */}
                      <div className="tracker-history-change">
                        <div className="tracker-history-user old">
                          <span className="tracker-history-user-label">Ancien:</span>
                          <span className="tracker-history-user-name">
                            {formatUserName(entry.old_user)}
                          </span>
                        </div>

                        <ArrowRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />

                        <div className="tracker-history-user new">
                          <span className="tracker-history-user-label">Nouveau:</span>
                          <span className="tracker-history-user-name">
                            {formatUserName(entry.new_user)}
                          </span>
                        </div>
                      </div>

                      {/* Changed by */}
                      {entry.changed_by?.id && (
                        <div className="tracker-history-changed-by">
                          Modifié par: <strong>{formatUserName(entry.changed_by)}</strong>
                        </div>
                      )}

                      {/* Notes */}
                      {entry.notes && (
                        <div className="tracker-history-notes">
                          <span className="tracker-history-notes-label">Note:</span>
                          <span className="tracker-history-notes-text">{entry.notes}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="tracker-modal-footer">
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>
    </>
  );
};
